/**
 * הצעת שיווק לחבילה דינמית — נתונים מובנים ל־UI + טקסט להעתקה.
 * מקור אמת לתאריכי שהייה: searchPhrase.checkIn / checkOut בלבד.
 */

import {
  firstDefined,
  normalizeFlightSegment,
} from "../adapters/buildDynamicVacationPackageViewModel";
import { formatIsraelDisplayDate } from "../DatesHelpers";
import { legToPremiumSummary, legIsDirect } from "../premiumPackageCardModel";
import { resolveAirlineNameFromPackageLeg } from "../../Components/Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";
import { formatIlsDisplay } from "../formatIlsDisplay";
import { buildHotelSearchCardHeroImageUrls } from "../hotelSearchListDisplay";

export const MARKETING_OFFER_DISCLAIMER =
  "המחיר עשוי להשתנות בהתאם לזמינות ולמועד ההזמנה";

const BANNED_SUBSTRINGS = [
  "חלומי",
  "מושלם",
  "יוקרתי",
  "הכי טוב",
  "מפנק",
  "ללא דאגות",
];

const FALLBACK_FACILITIES = "כולל שירותי מלון בסיסיים";
const FALLBACK_ROOM = "חדר סטנדרטי";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function countPaxFromRooms(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) return 2;
  let n = 0;
  rooms.forEach((room) => {
    n += toNum(room.AdultsCount ?? room.adults ?? 0);
    const kids = room.KidsAges?.length ?? room.kids ?? 0;
    n += toNum(kids);
  });
  return Math.max(1, n);
}

function audienceLabelHe(searchPhrase) {
  const rooms = searchPhrase?.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return "הרכב נוסעים לפי החיפוש";
  }
  let adults = 0;
  let kids = 0;
  rooms.forEach((r) => {
    adults += toNum(r.AdultsCount ?? r.adults ?? 0);
    kids += toNum(r.KidsAges?.length ?? r.kids ?? 0);
  });
  if (kids > 0) {
    return "משפחות עם ילדים או קבוצות קטנות (לפי פרטי החיפוש)";
  }
  if (adults === 2) return "זוג או שני מבוגרים";
  if (adults === 1) return "נוסע יחיד";
  return `${adults} מבוגרים (לפי החיפוש)`;
}

function splitDateTime(full) {
  if (!full || typeof full !== "string") return ["", ""];
  const parts = full.trim().split(/\s+/);
  return [parts[0] || "", parts[1] || ""];
}

/** DD/MM/YYYY מתוך searchPhrase או מתאריך רגל — בלי היפוך יום/חודש */
function calendarDayFromSearchOrLeg(searchDay, leg) {
  const fromSearch = formatIsraelDisplayDate(searchDay);
  if (fromSearch) return fromSearch;
  const [d] = splitDateTime(leg?.depDate || "");
  return formatIsraelDisplayDate(d) || null;
}

function legTimeRangeLine(leg) {
  if (!leg) return null;
  const sum = legToPremiumSummary(leg);
  if (sum?.timeLine && sum.timeLine !== "— —") return sum.timeLine;
  return null;
}

function airlineLine(leg, airlines) {
  if (!leg) return null;
  const name =
    (leg.airlineName && String(leg.airlineName).trim()) ||
    resolveAirlineNameFromPackageLeg(leg, airlines || []);
  const code = leg.airlineCode != null ? String(leg.airlineCode).trim() : "";
  if (name) return sanitizeNoBanned(name);
  if (code) return sanitizeNoBanned(code);
  return null;
}

function baggageLineFromLeg(leg) {
  if (!leg || typeof leg !== "object") return null;
  const hand =
    leg.handBaggage ??
    leg.hand_baggage ??
    leg.cabinBaggage ??
    leg.cabin_baggage;
  const checked =
    leg.checkedBaggage ?? leg.checked_baggage ?? leg.baggageAllowance ?? leg.baggage;
  const parts = [];
  if (hand != null && String(hand).trim()) parts.push(`תיק יד: ${String(hand).trim()}`);
  if (checked != null && String(checked).trim())
    parts.push(`מזוודה: ${String(checked).trim()}`);
  if (parts.length) return parts.join(" · ");
  const raw = leg.baggageText ?? leg.baggageInfo ?? leg.baggageDescription;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return null;
}

function extractFacilities(hotelsItem, max = 3) {
  const raw =
    hotelsItem?.Amenities ??
    hotelsItem?.amenities ??
    hotelsItem?.Facilities ??
    hotelsItem?.facilities ??
    hotelsItem?.HotelFacilities ??
    hotelsItem?.hotelFacilities;
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const x of raw) {
    const label =
      typeof x === "string"
        ? x
        : x?.name ??
          x?.Name ??
          x?.facilityname ??
          x?.FacilityName ??
          x?.label ??
          "";
    const s = String(label).trim();
    if (s && !out.includes(s)) out.push(s);
    if (out.length >= max) break;
  }
  return out;
}

function starsEmoji(count) {
  const n = Math.min(5, Math.max(0, Math.round(Number(count) || 0)));
  return n > 0 ? "⭐".repeat(n) : "";
}

export function sanitizeNoBanned(text) {
  let t = String(text || "");
  BANNED_SUBSTRINGS.forEach((w) => {
    if (t.includes(w)) {
      t = t.split(w).join("");
    }
  });
  return t.replace(/\s+/g, " ").trim();
}

/** שורת תאריכי שהייה — רק מ־searchPhrase (מקור אמת), תמיד DD/MM/YYYY */
export function formatStayDatesFromSearch(searchPhrase) {
  const cin = formatIsraelDisplayDate(
    searchPhrase?.checkIn ?? searchPhrase?.CheckIn
  );
  const cout = formatIsraelDisplayDate(
    searchPhrase?.checkOut ?? searchPhrase?.CheckOut
  );
  if (cin && cout) return `${cin} – ${cout}`;
  if (cin) return cin;
  return "לפי תאריכי החיפוש";
}

function pickHeroImageUrl(hotelsItem) {
  const urls = buildHotelSearchCardHeroImageUrls(hotelsItem || {});
  if (urls[0]) return urls[0];
  const img0 = hotelsItem?.Images?.[0]?.FullSize ?? hotelsItem?.images?.[0];
  if (typeof img0 === "string" && img0.trim()) return img0.trim();
  if (img0 && typeof img0 === "object") {
    const u = img0.FullSize || img0.url;
    if (u && String(u).trim()) return String(u).trim();
  }
  return null;
}

/** @returns {object} מודל תצוגה להצעה — כולל header, flights, hotel, room, price */
export function buildMarketingOfferViewModel(input = {}) {
  const {
    vm = null,
    hotelsItem = null,
    searchPhrase = null,
    selectedDepart = null,
    selectedReturn = null,
    airlines = [],
    roomLineOverride = null,
    hotelSnapshot = null,
    selectedOffer = null,
    /** כש־true (חבילת הלוך־חזור) — מציגים בלוק חזור גם לפני בחירת טיסה */
    requireReturnFlight = false,
  } = input;

  const depLeg = normalizeFlightSegment(selectedDepart);
  const retLeg = normalizeFlightSegment(selectedReturn);

  const snapName = hotelSnapshot?.hotelName;
  const snapStars = hotelSnapshot?.stars;
  const snapLine =
    hotelSnapshot?.locationLine ||
    [hotelSnapshot?.locationCity, hotelSnapshot?.locationCountry]
      .filter(Boolean)
      .join(", ") ||
    null;

  const checkIn =
    searchPhrase?.checkIn ?? searchPhrase?.CheckIn ?? null;
  const checkOut =
    searchPhrase?.checkOut ?? searchPhrase?.CheckOut ?? null;

  const destinationFriendly =
    sanitizeNoBanned(
      [searchPhrase?.city, searchPhrase?.country].filter(Boolean).join(", ") ||
        [vm?.locationCity, vm?.locationCountry].filter(Boolean).join(", ") ||
        snapLine ||
        vm?.locationLine ||
        "יעד נבחר"
    ) || "יעד נבחר";

  const area = sanitizeNoBanned(
    vm?.locationLine ||
      snapLine ||
      [hotelSnapshot?.locationCity, hotelSnapshot?.locationCountry]
        .filter(Boolean)
        .join(" · ") ||
      vm?.locationCity ||
      destinationFriendly
  );

  const datesLine = formatStayDatesFromSearch(searchPhrase);

  const outboundCalendarDate = depLeg
    ? calendarDayFromSearchOrLeg(checkIn, depLeg)
    : null;
  const inboundCalendarDate = retLeg
    ? calendarDayFromSearchOrLeg(checkOut, retLeg)
    : null;

  const showInboundSection = Boolean(retLeg) || requireReturnFlight === true;

  const outRoute = depLeg ? legToPremiumSummary(depLeg)?.routeLine : null;
  const inRoute = retLeg ? legToPremiumSummary(retLeg)?.routeLine : null;
  const outTime = legTimeRangeLine(depLeg);
  const inTime = legTimeRangeLine(retLeg);
  const outAirline = airlineLine(depLeg, airlines);
  const inAirline = airlineLine(retLeg, airlines);
  const outDirect = depLeg ? (legIsDirect(depLeg) ? "ישיר" : "עצירה / קונקשן") : null;
  const inDirect = retLeg ? (legIsDirect(retLeg) ? "ישיר" : "עצירה / קונקשן") : null;

  const bagOut = baggageLineFromLeg(depLeg);
  const bagIn = baggageLineFromLeg(retLeg);
  const bagUnique = [];
  [bagOut, bagIn].forEach((b) => {
    if (b && !bagUnique.includes(b)) bagUnique.push(b);
  });
  const baggageCombined =
    bagUnique.length > 0 ? sanitizeNoBanned(bagUnique.join(" · ")) : null;

  const hotelName =
    sanitizeNoBanned(
      selectedOffer?.hotelName ||
        snapName ||
        vm?.hotelName ||
        firstDefined(
          hotelsItem?.DisplayName,
          hotelsItem?.Name,
          hotelsItem?.HotelName,
          hotelsItem?.hotelName
        ) ||
        "—"
    ) || "—";

  const starsNumRaw =
    snapStars != null && Number.isFinite(Number(snapStars))
      ? Number(snapStars)
      : vm?.stars != null
        ? Number(vm.stars)
        : Number(hotelsItem?.StarRating);
  const starsCount = Number.isFinite(starsNumRaw) ? Math.min(5, Math.max(0, Math.round(starsNumRaw))) : 0;

  const board = sanitizeNoBanned(
    selectedOffer?.boardLabel || vm?.boardLabel || "—"
  ) || "—";

  const facilitiesRaw = extractFacilities(hotelsItem, 5);
  const facilities =
    facilitiesRaw.length > 0
      ? facilitiesRaw.slice(0, 3).map((f) => sanitizeNoBanned(f))
      : [FALLBACK_FACILITIES];

  const roomName =
    sanitizeNoBanned(selectedOffer?.roomLine || roomLineOverride) ||
    FALLBACK_ROOM;

  const pax = countPaxFromRooms(searchPhrase?.rooms);
  const audience = audienceLabelHe(searchPhrase);

  const selectedTotal = selectedOffer?.totalPriceIls;
  let amountDisplay = "—";
  let priceKind = "perPerson";

  if (
    selectedTotal != null &&
    Number.isFinite(Number(selectedTotal)) &&
    Number(selectedTotal) > 0
  ) {
    amountDisplay = formatIlsDisplay(Math.round(Number(selectedTotal)));
    priceKind = "total";
  } else {
    const finalIls = vm?.pricing?.finalPrice;
    let pricePerPersonIls = null;
    if (finalIls != null && Number.isFinite(finalIls) && finalIls > 0 && pax > 0) {
      pricePerPersonIls = Math.round(finalIls / pax);
    }
    if (pricePerPersonIls != null && pricePerPersonIls > 0) {
      amountDisplay = formatIlsDisplay(pricePerPersonIls);
    }
  }

  const heroImageUrl = pickHeroImageUrl(hotelsItem);

  const headlineHe = `חבילה ל־${destinationFriendly}`;
  const packageTaglineHe = "כולל טיסות ומלון";

  return {
    version: 1,
    header: {
      destinationDisplay: destinationFriendly,
      headlineHe,
      packageTaglineHe,
      datesLine,
      checkIn: formatIsraelDisplayDate(checkIn) || null,
      checkOut: formatIsraelDisplayDate(checkOut) || null,
    },
    heroImageUrl,
    flights: {
      showInboundSection,
      outbound: {
        labelHe: "טיסת הלוך",
        calendarDate: outboundCalendarDate,
        timeRange: outTime,
        routeLine: outRoute ? sanitizeNoBanned(outRoute) : null,
        airline: outAirline,
        directLabel: outDirect,
        hasLeg: Boolean(depLeg),
      },
      inbound: {
        labelHe: "טיסת חזור",
        calendarDate: inboundCalendarDate,
        timeRange: inTime,
        routeLine: inRoute ? sanitizeNoBanned(inRoute) : null,
        airline: inAirline,
        directLabel: inDirect,
        hasLeg: Boolean(retLeg),
      },
      baggageLine: baggageCombined,
      stayDatesNoteHe: `תאריכי שהייה במלון לפי החיפוש: ${datesLine}`,
    },
    hotel: {
      name: hotelName,
      starsCount,
      starsEmoji: starsEmoji(starsCount),
      locationLine: area,
      boardLabel: board,
      facilities,
    },
    room: {
      name: roomName,
      occupancyLabel: audience,
    },
    price: {
      amountDisplay,
      priceKind,
      disclaimer: MARKETING_OFFER_DISCLAIMER,
      titleHe:
        priceKind === "total"
          ? "מחיר החבילה"
          : "מחיר משוער לאדם",
      subTitleHe:
        priceKind === "total"
          ? "לפי חדר והצעה שנבחרו"
          : "לפי הרכב החדרים בחיפוש",
    },
    footerCtaHe: "ניתן לבדוק זמינות ולעדכן בהתאם לצרכים",
    _internal: {
      audienceLabel: audience,
      areaLine: area,
    },
  };
}

/**
 * טקסט נקי להדבקה (וואטסאפ) — נבנה מה־view model בלבד.
 * @param {ReturnType<typeof buildMarketingOfferViewModel>} viewModel
 */
export function buildMarketingOfferWhatsappText(viewModel) {
  if (!viewModel || viewModel.version !== 1) return "";
  const h = viewModel.header;
  const f = viewModel.flights;
  const hot = viewModel.hotel;
  const r = viewModel.room;
  const p = viewModel.price;
  const aud = viewModel._internal?.audienceLabel || "";
  const area = viewModel._internal?.areaLine || "";

  const lines = [];

  lines.push(`🌴 ${h.headlineHe} – ${h.packageTaglineHe}`);
  lines.push(`📅 ${h.datesLine}`);
  lines.push("");
  lines.push(
    `חבילה הכוללת טיסות ומלון באזור ${area}, מתאימה ל־${aud}.`
  );
  lines.push("");
  lines.push("✈️ טיסות:");
  lines.push(f.stayDatesNoteHe);
  lines.push("");

  const out = f.outbound;
  const outParts = [];
  if (out.calendarDate) outParts.push(`תאריך: ${out.calendarDate}`);
  if (out.timeRange) outParts.push(`שעות: ${out.timeRange}`);
  if (out.routeLine) outParts.push(`מסלול: ${out.routeLine}`);
  if (out.airline) outParts.push(`חברת תעופה: ${out.airline}`);
  if (out.directLabel) outParts.push(out.directLabel);
  lines.push(`הלוך${outParts.length ? ": " + outParts.join(" · ") : ": —"}`);

  if (f.showInboundSection) {
    const inn = f.inbound;
    const inParts = [];
    if (inn.calendarDate) inParts.push(`תאריך: ${inn.calendarDate}`);
    if (inn.timeRange) inParts.push(`שעות: ${inn.timeRange}`);
    if (inn.routeLine) inParts.push(`מסלול: ${inn.routeLine}`);
    if (inn.airline) inParts.push(`חברת תעופה: ${inn.airline}`);
    if (inn.directLabel) inParts.push(inn.directLabel);
    lines.push(`חזור${inParts.length ? ": " + inParts.join(" · ") : ": —"}`);
  }

  if (f.baggageLine) lines.push(`כבודה: ${f.baggageLine}`);
  lines.push("");
  lines.push(`🏨 מלון: ${hot.name}${hot.starsEmoji ? ` ${hot.starsEmoji}` : ""}`);
  lines.push(`📍 מיקום: ${hot.locationLine}`);
  lines.push(`🍽️ בסיס אירוח: ${hot.boardLabel}`);
  lines.push("");
  lines.push("מה כולל המלון:");
  hot.facilities.forEach((x) => lines.push(`• ${x}`));
  lines.push("");
  lines.push(`🛏️ חדר: ${r.name}`);
  lines.push(`מתאים ל: ${r.occupancyLabel}`);
  lines.push("");
  if (p.priceKind === "total") {
    lines.push(`💰 מחיר החבילה (לפי חדר שנבחר): ${p.amountDisplay}`);
  } else {
    lines.push(`💰 מחיר: ${p.amountDisplay} לאדם`);
  }
  lines.push(`*${p.disclaimer}`);
  lines.push("");
  lines.push(`👉 ${viewModel.footerCtaHe}`);

  return lines
    .join("\n")
    .replace(/\bundefined\b|\bnull\b/gi, "")
    .replace(/[^\S\r\n]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n");
}

/** מחרוזות שורה אחת לתאימות לאחור (בדיקות / לוגים) */
function flightSummaryLine(prefix, row) {
  const parts = [];
  if (row.calendarDate) parts.push(row.calendarDate);
  if (row.timeRange) parts.push(row.timeRange);
  if (row.airline) parts.push(row.airline);
  return `${prefix}: ${parts.length ? parts.join(" · ") : "—"}`;
}

/**
 * @deprecated מעדיף buildMarketingOfferViewModel + buildMarketingOfferWhatsappText
 */
export function generateMarketingOffer(input = {}) {
  const viewModel = buildMarketingOfferViewModel(input);
  const whatsappText = buildMarketingOfferWhatsappText(viewModel);
  const f = viewModel.flights;
  return {
    title: `${viewModel.header.headlineHe} – ${viewModel.header.packageTaglineHe}`,
    subtitle: viewModel.header.datesLine,
    flights: {
      outboundSummary: flightSummaryLine("הלוך", f.outbound),
      inboundSummary: f.showInboundSection
        ? flightSummaryLine("חזור", f.inbound)
        : null,
      baggage: f.baggageLine,
    },
    hotel: {
      name: viewModel.hotel.name,
      starsDisplay: viewModel.hotel.starsEmoji,
      location: viewModel.hotel.locationLine,
      board: viewModel.hotel.boardLabel,
    },
    features: viewModel.hotel.facilities,
    room: viewModel.room.name,
    audienceLabel: viewModel.room.occupancyLabel,
    price: {
      perPersonDisplay: viewModel.price.amountDisplay,
      priceKind: viewModel.price.priceKind,
      disclaimer: viewModel.price.disclaimer,
    },
    disclaimer: viewModel.price.disclaimer,
    whatsappText,
    viewModel,
  };
}
